import React from 'react';
import type { GoalProgress, ReportDetailLabels } from '../../../shared/types/reports';

export interface GoalProgressRingsProps {
  goals: GoalProgress[];
  labels: ReportDetailLabels;
}

export function GoalProgressRings({ goals, labels }: GoalProgressRingsProps) {
  if (goals.length === 0) {
    return null;
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white border-0">
        <h5 className="mb-0">
          <i className="bi bi-bullseye me-2"></i>
          {labels.goalProgress}
        </h5>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {goals.map((goal, index) => (
            <div key={index} className="col-6 col-md-4 col-lg-3">
              <GoalRing goal={goal} labels={labels} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface GoalRingProps {
  goal: GoalProgress;
  labels: ReportDetailLabels;
}

function GoalRing({ goal, labels }: GoalRingProps) {
  const percentage = Math.min(goal.completionPercentage, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const isAchieved = goal.achieved;

  return (
    <div className="text-center">
      <div className="position-relative d-inline-block">
        <svg width="100" height="100" className="progress-ring">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#e9ecef"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={isAchieved ? '#198754' : '#0d6efd'}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div
          className="position-absolute top-50 start-50 translate-middle text-center"
          style={{ width: '70px' }}
        >
          <div className="fw-bold fs-5">{Math.round(percentage)}%</div>
          <div className="small text-muted">
            {goal.actualCount}/{goal.targetCount}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <div className="fw-semibold text-truncate" title={goal.fieldName}>
          {goal.fieldName}
        </div>
        {isAchieved ? (
          <span className="badge bg-success">
            <i className="bi bi-check-circle me-1"></i>
            {labels.achieved}
          </span>
        ) : (
          <span className="badge bg-secondary">
            {labels.notAchieved}
          </span>
        )}
      </div>
    </div>
  );
}

export default GoalProgressRings;
